// src/router/index.ts
import { isPermissionsConfigurationPath, resolveUserRouteAccessLevel } from '@/utils/userRouteAccess';
import NProgress from 'nprogress';
import { createRouter, createWebHistory } from 'vue-router';
import { protectedRoutes, publicRoutes, specialRoutes } from './routes';

// Combinar todas as rotas
const allRoutes = [...publicRoutes, ...protectedRoutes, ...specialRoutes];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: allRoutes
});

// Delay mínimo de 1 segundo para transições entre páginas
const MIN_TRANSITION_DELAY = 1000;
let transitionStartTime: number | null = null;

// Intervalo de verificação de status do usuário: 5 minutos
const CHECK_STATUS_INTERVAL = 5 * 60 * 1000;

// Flag para evitar chamadas de checkUserStatus simultâneas
let isCheckingUserStatus = false;

// Wrapper para NProgress.start()
const startProgress = () => {
    transitionStartTime = Date.now();
    NProgress.start();
};

// Aguardar delay mínimo antes de permitir navegação
const waitMinimumDelay = async (): Promise<void> => {
    if (transitionStartTime === null) return;
    const elapsed = Date.now() - transitionStartTime;
    const remaining = MIN_TRANSITION_DELAY - elapsed;
    if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
    }
};

// Finalizar NProgress
const doneProgress = () => {
    NProgress.done();
    transitionStartTime = null;
};

// Guard de autenticação e verificação de permissões
// Fluxo linear:
// 1. Não autenticado? → /login
// 2. Admin Master → carrega ACL; se nível efetivo é `none` (ex.: rota inativa em `routes`), redireciona (exceto /permissions)
// 3. Demais usuários: mesmo mapa (/me)
//    a. accessLevel !== 'none' → permitir
//    b. accessLevel === 'none' → redirecionar (nunca next(false) — evita tela branca)
//    c. Rota não encontrada no mapa → fail-secure
router.beforeEach(async (to, from, next) => {
    // Iniciar barra de progresso apenas se mudou de rota
    if (to.path !== from.path) {
        startProgress();
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const hideForAuth = to.matched.some((record) => record.meta.hideForAuth);

    // Importar o store de autenticação
    const { useAuthStore } = await import('@/stores/authStore');
    const authStore = useAuthStore();

    // Inicializar o store e aguardar
    await authStore.initializeAuth();

    const isAuthenticated = authStore.isAuthenticated;

    // === PASSO 1: Verificar autenticação ===
    if (requiresAuth) {
        if (!isAuthenticated) {
            await waitMinimumDelay();
            next({ path: '/login', query: { redirect: to.fullPath } });
            return;
        }

        // Rota kiosk: qualquer usuário autenticado acessa sem checagem de permissões
        if (to.meta.kiosk === true) {
            await waitMinimumDelay();
            next();
            return;
        }

        // Rota livre p/ autenticados (ex.: gestor de cartão vê só as faturas dele,
        // sem necessidade de permissão de rota — controle é por atribuição no backend)
        if (to.matched.some((r) => r.meta.freeForAuth === true)) {
            await waitMinimumDelay();
            next();
            return;
        }

        // Multi-tenant (US4): bloquear rota de MÓDULO desligado para o tenant (inclui admin
        // master — mas na Atlan todos os módulos estão ativos). O backend já responde 404;
        // aqui evitamos abrir uma tela quebrada. CORE não tem slug → nunca bloqueia.
        try {
            const { useTenantStore } = await import('@/stores/tenantStore');
            const { moduleSlugForPath } = await import('@/config/moduleRegistry');
            const tenantStore = useTenantStore();

            // Rotas exclusivas do tenant master (administração de tenants): só na Atlan.
            // O backend também barra (requireMasterTenant → 404); aqui evitamos a tela.
            if (to.meta?.masterOnly && tenantStore.loaded && !tenantStore.isMaster) {
                await waitMinimumDelay();
                next('/');
                return;
            }

            const moduleSlug = moduleSlugForPath(to.path);
            if (tenantStore.loaded && moduleSlug && !tenantStore.enabledModules.includes(moduleSlug)) {
                await waitMinimumDelay();
                next('/');
                return;
            }
        } catch {
            /* tenantStore indisponível — backend ainda enforça via 404 */
        }

        // === PASSO 2: Admin Master — respeita rotas desativadas (`routes.is_active`) via /me, exceto /permissions
        if (authStore.isAdminMaster) {
            await checkUserStatusIfNeeded(authStore);
            await authStore.fetchUserRouteLevelPaths();
            const accessLevel = resolveUserRouteAccessLevel(to.path, authStore.userAccessMap);
            if (accessLevel === 'none' && !isPermissionsConfigurationPath(to.path)) {
                await waitMinimumDelay();
                if (to.path === '/') {
                    next();
                } else {
                    next('/');
                }
                return;
            }
            await waitMinimumDelay();
            next();
            return;
        }

        // === PASSO 3: Verificar status do usuário ===
        const isActive = await checkUserStatusIfNeeded(authStore);
        if (!isActive) {
            authStore.logout();
            await waitMinimumDelay();
            next('/login');
            return;
        }

        // === PASSO 4: Verificar permissão da rota via userAccessMap ===
        // Garantir que as permissões estão carregadas
        await authStore.fetchUserRouteLevelPaths();

        const accessLevel = resolveUserRouteAccessLevel(to.path, authStore.userAccessMap);

        if (accessLevel === 'none') {
            await waitMinimumDelay();
            if (to.path === '/') {
                next();
            } else {
                next('/');
            }
            return;
        }

        if (accessLevel && accessLevel !== 'none') {
            // Tem acesso (member, editor ou admin)
            await waitMinimumDelay();
            next();
            return;
        }

        // Rota não encontrada no mapa → fail-secure (bloquear)
        // Isso acontece quando a rota não está cadastrada no banco.
        // '/' é exceção: permite acesso a usuários autenticados para evitar loop
        // (userAccessMap vazio em banco novo ou antes do syncFrontendRoutes do admin)
        console.warn(`⚠️ Rota ${to.path} não encontrada no mapa de permissões - bloqueando (fail-secure)`);
        await waitMinimumDelay();
        if (to.path === '/') {
            next();
        } else {
            next('/');
        }
        return;
    }

    // Se a rota deve ser escondida quando autenticado (ex: login)
    if (hideForAuth && isAuthenticated) {
        await waitMinimumDelay();
        next('/');
        return;
    }

    // Rota pública - permitir acesso
    await waitMinimumDelay();
    next();
});

/**
 * Verifica o status do usuário a cada CHECK_STATUS_INTERVAL.
 * Retorna true se o usuário está ativo, false caso contrário.
 */
async function checkUserStatusIfNeeded(authStore: any): Promise<boolean> {
    const lastCheck = sessionStorage.getItem('lastUserCheck');
    const now = Date.now();
    const shouldCheck = !lastCheck || now - parseInt(lastCheck) > CHECK_STATUS_INTERVAL;

    if (!shouldCheck || isCheckingUserStatus) return true;

    isCheckingUserStatus = true;

    try {
        const isUserActive = await authStore.checkUserStatus();

        if (!isUserActive) {
            return false;
        }

        sessionStorage.setItem('lastUserCheck', now.toString());
        return true;
    } catch {
        // Em caso de erro de rede, manter sessão (fail-open para status)
        return true;
    } finally {
        isCheckingUserStatus = false;
    }
}

// Barra de progresso - finalizar após navegação
router.afterEach(() => {
    doneProgress();
});

// Barra de progresso - finalizar em caso de erro
// Após um deploy, navegadores com index.html cacheado podem referenciar
// chunks (hash antigo) que não existem mais no servidor. O Vue Router
// captura esse erro aqui — recarregar a página força o navegador a buscar
// o index.html novo e os chunks corretos.
router.onError((err) => {
    doneProgress();
    const msg = err instanceof Error ? err.message : String(err ?? '');
    const isChunkLoadError =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /Loading chunk \S+ failed/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg);

    if (isChunkLoadError) {
        const RELOAD_KEY = 'chunkReloadAt';
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > 10_000) {
            sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
            window.location.reload();
        }
    }
});

export default router;
